// ─────────────────────────────────────────────────────────────────────────────
// algolia-observers.js — CB Shopify theme
//
// All MutationObserver re-init wiring for custom elements that Algolia renders.
// Algolia's html tagged template builds DOM nodes and then appends children, so
// custom element constructors fire before children exist. Each observer below
// detects newly inserted instances and completes initialization once children
// are present.
//
// Per CLAUDE.md: every new custom element rendered by Algolia needs a matching
// observer here, using the data-algolia-init guard pattern.
// ─────────────────────────────────────────────────────────────────────────────

document.addEventListener('algolia.hooks.initialize', function () {
  // Show the facets toolbar once Algolia has rendered hits (avoids empty sort/stats flash).
  const facetsToolbarObserver = new MutationObserver(() => {
    const toolbar = document.querySelector('[data-algolia-facets-toolbar]')
    if (!toolbar) return
    const hasHits = document.querySelector(
      '.ais-hits-container .ais-Hits-item, .ais-hits-container .ais-results-as-block > *'
    )
    const hasStats = document.querySelector('.ais-stats-container .ais-Stats')
    if (hasHits || hasStats) {
      toolbar.style.display = ''
      facetsToolbarObserver.disconnect()
    }
  })
  facetsToolbarObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // Re-initialize <menu-drawer> after Algolia renders it into the DOM.
  // The custom element constructor fires when the element node is *created*
  // (before children are appended by Algolia's html template), so
  // mainDetailsToggle is null and bindEvents() runs against an empty element.
  // We watch for newly inserted <menu-drawer> elements where mainDetailsToggle
  // is still null, then complete initialization once children are present.
  // data-algolia-init guards against double-binding and handles IS re-renders.
  const menuDrawerReinitObserver = new MutationObserver(() => {
    document
      .querySelectorAll('menu-drawer:not([data-algolia-init])')
      .forEach((drawer) => {
        if (drawer.mainDetailsToggle) {
          drawer.setAttribute('data-algolia-init', '') // native drawer: already init, just mark
          return
        }
        const details = drawer.querySelector('details')
        if (!details) return // children not yet appended — will retry on next mutation
        drawer.setAttribute('data-algolia-init', '')
        drawer.mainDetailsToggle = details
        drawer.bindEvents()
      })
  })
  menuDrawerReinitObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // Re-initialize <modal-opener> after Algolia renders it.
  // ModalOpener constructor: this.querySelector('button') — returns null before children exist.
  // We reinit any uninitialized instances after each DOM mutation.
  // data-algolia-init guards against double-binding on re-query.
  const modalOpenerReinitObserver = new MutationObserver(() => {
    document
      .querySelectorAll('modal-opener:not([data-algolia-init])')
      .forEach((opener) => {
        const button = opener.querySelector('button')
        if (!button) return
        opener.setAttribute('data-algolia-init', '')
        button.addEventListener('click', () => {
          const modal = document.querySelector(
            opener.getAttribute('data-modal')
          )
          if (modal) modal.show(button)
        })
      })
  })
  modalOpenerReinitObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // Fix up <quick-add-modal> children references after Algolia appends them.
  // ModalDialog's constructor now has a null-safe querySelector (fixed in global.js),
  // but this.modalContent is still null until children exist. Assign it here once present.
  // connectedCallback moves the modal to document.body — we watch there for newly connected instances.
  const quickAddModalReinitObserver = new MutationObserver(() => {
    document
      .querySelectorAll('quick-add-modal:not([data-algolia-init])')
      .forEach((modal) => {
        const closeBtn = modal.querySelector('[id^="ModalClose-"]')
        const contentDiv = modal.querySelector('[id^="QuickAddInfo-"]')
        if (!closeBtn || !contentDiv) return
        modal.setAttribute('data-algolia-init', '')
        modal.modalContent = contentDiv
        closeBtn.addEventListener('click', () => modal.hide(false))
      })
  })
  quickAddModalReinitObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // Re-initialize color-swatch-variant.productCard after Algolia appends elements to DOM.
  // The constructor's this.closest('.product-grid-item') returns null before connection.
  // Scoped to [data-algolia-handle] descendants — native cards outside Algolia hits are unaffected.
  const colorSwatchReinitObserver = new MutationObserver(() => {
    document
      .querySelectorAll(
        '[data-algolia-handle] color-swatch-variant:not([data-algolia-init])'
      )
      .forEach((el) => {
        el.setAttribute('data-algolia-init', '')
        el.productCard = el.closest('[data-algolia-handle]')
      })
  })
  colorSwatchReinitObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // Populate mobile sort select once the Algolia SortBy widget mounts to its desktop container,
  // then keep it in sync. The Algolia app only mounts to .ais-sort-orders-container, so we mirror
  // the select options into [data-algolia-mobile-sort] and sync via DOM events.
  const mobileSortObserver = new MutationObserver(() => {
    const desktopSelect = document.querySelector(
      '.ais-sort-orders-container .ais-SortBy-select'
    )
    if (!desktopSelect || !desktopSelect.options.length) return
    document
      .querySelectorAll('[data-algolia-mobile-sort]:not([data-algolia-init])')
      .forEach((mobileSelect) => {
        mobileSelect.setAttribute('data-algolia-init', '')
        Array.from(desktopSelect.options).forEach((opt) => {
          mobileSelect.add(
            new Option(opt.text, opt.value, opt.defaultSelected, opt.selected)
          )
        })
        mobileSelect.value = desktopSelect.value
        mobileSelect.addEventListener('change', () => {
          desktopSelect.value = mobileSelect.value
          desktopSelect.dispatchEvent(new Event('change', { bubbles: true }))
        })
      })
    // Desktop select is mounted and all mobile selects are synced — disconnect to stop
    // observing every subsequent DOM mutation for no reason.
    mobileSortObserver.disconnect()
  })
  mobileSortObserver.observe(document.body, { childList: true, subtree: true })

  // Sync desktop → all mobile selects when Algolia updates the sort (e.g. via URL or init)
  document.addEventListener('change', (e) => {
    if (!e.target.matches('.ais-SortBy-select')) return
    document
      .querySelectorAll('[data-algolia-mobile-sort]')
      .forEach((mobileSelect) => {
        if (mobileSelect !== e.target) mobileSelect.value = e.target.value
      })
  })

  // Populate / depopulate the .ais-clear-refinements-container placeholders (desktop
  // active-facets bar and mobile drawer footer) whenever active-refinement pills change.
  //
  // The Algolia Shopify plugin auto-mounts .ais-current-refined-values-container but does
  // NOT auto-mount a ClearRefinements widget. We inject a bare <a class="ais-clear-all-btn">
  // styled standalone in algolia-overrides.css — deliberately without the native
  // .active-facets__button-remove / .underlined-link classes, whose padding and
  // always-on underline would otherwise need overriding.
  const clearAllLabel =
    (window.algoliaSettings || {}).clearAllLabel || 'Remove all'

  const updateClearAllButtons = () => {
    const pillsContainer = document.querySelector(
      '.ais-current-refined-values-container'
    )
    const containers = document.querySelectorAll(
      '.ais-clear-refinements-container'
    )
    if (!pillsContainer || !containers.length) return

    // The IS widget always mounts a root <div> into the container even when no filters
    // are active, so .children.length > 0 is always true. Check for actual pill <li>
    // items (.ais-current-refined-values--item) instead.
    const hasPills = !!pillsContainer.querySelector(
      '.ais-current-refined-values--item'
    )

    // Show / hide the entire active-facets row so it never takes up space when empty.
    const activeFacetsRow = pillsContainer.closest('.active-facets')
    if (activeFacetsRow) {
      activeFacetsRow.style.display = hasPills ? '' : 'none'
    }

    containers.forEach((container) => {
      if (hasPills) {
        container.style.display = ''
        if (!container.querySelector('.ais-clear-all-btn')) {
          // A real href to the bare pathname strips every IS refinement query param,
          // so plain link navigation clears all filters — no click handler needed.
          const a = document.createElement('a')
          a.href = window.location.pathname
          a.className = 'ais-clear-all-btn'
          a.textContent = clearAllLabel
          container.appendChild(a)
        }
      } else {
        container.style.display = 'none'
        container.innerHTML = ''
      }
    })
  }

  const clearRefinementsObserver = new MutationObserver(updateClearAllButtons)
  clearRefinementsObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })
})
